import Phaser from 'phaser';
import { SCENE, STREET_SPAWNS } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { showToast } from '../ui/Toast.js';
import { showModal } from '../ui/Modal.js';
import { messageFor } from '../api/errors.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { reportLocation } from '../ui/locationTracker.js';
import { ANIMAL_EMOJI, BUDA_ACC } from '../ui/animalCopy.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { shopRoom, shopGrid, buyState } from '../ui/shopInterior.js';

const ANIMAL_IDS = ['pies', 'kot'];        // 4c: chickens and cows are bought in the farm's Rynek
const BUDA_IDS = ['buda', 'kocie'];
const FOOD_IDS = ['ziarno', 'siano', 'mieso'];

// Odmiana nazw bud do komunikatów — polski wymaga przypadków, więc nie da się
// tego wyprowadzić z mianownika (name_pl w katalogu).
const BUDA_GEN = { kurnik: 'kurnika', obora: 'obory', buda: 'budy', kocie: 'legowiska' }; // "Brak ___"

export class PetShopScene extends Phaser.Scene {
  constructor() { super(SCENE.PET_SHOP); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create() {
    const exit = () => this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.PET });
    const { layer } = shopRoom(this, {
      bgKey: this.textures.exists('bg_pet_shop') ? 'bg_pet_shop' : 'bg_shop',
      title: '{paw} Sklep zoologiczny', onExit: exit,
    });
    // W sklepie nie chodzimy — brak postaci Natalki. Tabs and the Wszystko order: handoff 1b.
    const tabs = [
      { id: 'wszystko', label: 'Wszystko' }, { id: 'zwierzeta', label: '{paw} Zwierzęta' },
      { id: 'budy', label: '{house} Budy' }, { id: 'karma', label: '{wheat} Karma' },
    ];
    shopGrid(this, layer, { tabs, cardsFor: (tab) => this._cards(tab) });

    new MoneyBar(this);
    reportLocation(this, null, null, { immediate: true });
    attachCoordsDebug(this);
  }

  _cards(tab) {
    const cat = gameState.raw?.catalog ?? {};
    const inv = gameState.raw?.inventory ?? {};
    const placements = gameState.raw?.placements ?? [];
    const occupied = new Set((gameState.raw?.animals ?? []).map((a) => a.buda_placement_id));
    const card = (kind, id, spec, sub, onTap) => ({
      key: `${kind}_${id}`, tex: `${kind}_${id}`, name: spec.name_pl, sub,
      foot: `{coin} ${spec.price}`, state: buyState(spec.price), onTap,
    });
    const out = [];
    if (tab === 'wszystko' || tab === 'zwierzeta') {
      for (const id of ANIMAL_IDS) {
        const spec = cat.animals?.[id]; if (!spec) continue;
        const free = placements.filter((p) => p.type === 'buda' && p.item === spec.buda_id && !occupied.has(p.id)).length;
        const waiting = inv.unplaced_animals?.[id] ?? 0;
        const gen = BUDA_GEN[spec.buda_id] ?? this._budaName(spec.buda_id);
        out.push(card('animal', id, spec, `wolne ${gen}: ${free}${waiting ? `, czeka: ${waiting}` : ''}`, () => this._tryBuyAnimal(id)));
      }
    }
    if (tab === 'wszystko' || tab === 'budy') {
      for (const id of BUDA_IDS) {
        const spec = cat.budas?.[id]; if (!spec) continue;
        out.push(card('buda', id, spec, `nie postawionych: ${inv.unplaced_budas?.[id] ?? 0}`, () => this._buyBuda(id)));
      }
    }
    if (tab === 'wszystko' || tab === 'karma') {
      for (const id of FOOD_IDS) {
        const spec = cat.foods?.[id]; if (!spec) continue;
        out.push(card('food', id, spec, `masz: ${inv.foods?.[id] ?? 0}`, () => this._buyFood(id)));
      }
    }
    return out;
  }

  async _buyBuda(id) {
    try {
      await gameState.buyBuda(id);
      showToast(this, `Kupiono ${BUDA_ACC[id] ?? this._budaName(id)}! Postaw w zagrodzie {chicken} (lub psa/kota w domu)`);
    } catch (e) { showToast(this, messageFor(e)); }
  }

  async _buyFood(id) {
    try {
      await gameState.buyFood(id);
      showToast(this, 'Kupiono karmę {check}');
    } catch (e) { showToast(this, messageFor(e)); }
  }

  // "<Nazwa> czeka — postaw <BUDA_ACC>! <emoji>" — shown whenever the animal has no free
  // house yet and waits (inventory.unplaced_animals) until one is placed.
  _pocketToast(animalType, animalCat, requiredBudaId) {
    const acc = BUDA_ACC[requiredBudaId] ?? this._budaName(requiredBudaId);
    return `${animalCat.name_pl} czeka — postaw ${acc}! ${ANIMAL_EMOJI[animalType] ?? ''}`;
  }

  async _tryBuyAnimal(animalType) {
    const animalCat = gameState.raw?.catalog?.animals?.[animalType];
    if (!animalCat) return;
    const requiredBudaId = animalCat.buda_id;
    const placements = gameState.raw?.placements ?? [];
    const animalsList = gameState.raw?.animals ?? [];
    const occupied = new Set(animalsList.map(a => a.buda_placement_id));
    const candidates = placements.filter(p => p.type === 'buda' && p.item === requiredBudaId && !occupied.has(p.id));
    if (candidates.length === 0) {
      // No free house yet → the animal waits in the pocket and moves in when one is placed.
      try {
        await gameState.buyAnimal(animalType, null);
        showToast(this, this._pocketToast(animalType, animalCat, requiredBudaId));
      } catch (e) { showToast(this, messageFor(e)); }
      return;
    }
    let chosen = candidates[0];
    if (candidates.length > 1) {
      const pick = await showModal(this, {
        title: `Wybierz ${BUDA_ACC[requiredBudaId] ?? this._budaName(requiredBudaId)}`,
        body: `Masz ${candidates.length} wolnych. Wybiorę pierwszą.`,
        confirmLabel: 'OK', cancelLabel: 'Anuluj',
      });
      if (!pick) return;
    }
    try {
      // The chosen house may have gone stale (taken/removed) between the click and the
      // request — the backend then pockets the animal instead of raising. Detect that
      // by diffing the pocket count so the toast matches what actually happened.
      const before = gameState.raw?.inventory?.unplaced_animals?.[animalType] ?? 0;
      await gameState.buyAnimal(animalType, chosen.id);
      const after = gameState.raw?.inventory?.unplaced_animals?.[animalType] ?? 0;
      if (after > before) {
        showToast(this, this._pocketToast(animalType, animalCat, requiredBudaId));
      } else {
        showToast(this, `Kupiono ${animalCat.name_pl}! {party}`);
      }
    } catch (e) { showToast(this, messageFor(e)); }
  }

  _budaName(id) {
    return gameState.raw?.catalog?.budas?.[id]?.name_pl ?? id;
  }
}
